import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  derivePopupPaymentState,
  type PopupPaymentRowLike,
} from "@/lib/popupPaymentState";

export interface ExplainPaymentRow extends PopupPaymentRowLike {
  full_name: string;
  email?: string;
  amount_due: number;
}

interface ExplainPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ExplainPaymentRow | null;
}

const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }) : "—";

export function ExplainPaymentDialog({ open, onOpenChange, row }: ExplainPaymentDialogProps) {
  if (!row) return null;
  const state = derivePopupPaymentState(row);
  const hasStripe = !!row.stripe_payment_id;

  const rows: Array<[string, React.ReactNode]> = [
    ["Current Status", (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${state.className}`}>
        <span>{state.dot}</span>
        {state.label}
      </span>
    )],
    ["Vendor", row.full_name],
    ...(row.email ? [["Email", row.email] as [string, React.ReactNode]] : []),
    ["Amount Due", fmtMoney(row.amount_due)],
    ["Registration Created", fmtDate(row.created_at)],
    ["Last Activity", fmtDate(row.updated_at ?? row.created_at)],
    ["Payment Attempts", hasStripe ? "1 (Stripe session started)" : "None"],
  ];

  if (state.key === "processing" || state.key === "paid" || state.key === "failed") {
    rows.push(["Stripe Payment ID", row.stripe_payment_id || "—"]);
  }

  if (state.key === "processing") {
    rows.push(
      ["Authorized", "Yes"],
      ["Captured", "Pending"],
      ["Webhook Received", "No"],
      ["Expected Completion", "Usually within several minutes."],
    );
  }

  if (state.key === "paid") {
    rows.push(
      ["Captured", "Yes"],
      ["Webhook Received", "Yes"],
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Payment Status</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[140px_1fr] gap-3">
              <div className="text-muted-foreground">{label}:</div>
              <div className="font-medium break-all">{value}</div>
            </div>
          ))}

          <div className="mt-4 rounded-md border bg-muted/40 p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Recommended Action
            </div>
            <div className="text-sm">{state.recommendedAction}</div>
          </div>

          <div className="text-xs text-muted-foreground pt-2 border-t">
            {state.tooltip}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
